import { faToggleOn, faToggleOff, faRefresh, faUser, faUserCircle, faBookOpen, faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { Card, Row, Col } from 'react-bootstrap';
import StatsCard from '../../component/Cards/StatsCard';
import StatsCardWithProgress from '../../component/Cards/StatsCardWithProgress';
import DonutChart, { ChartDataItem } from '../../component/Charts/DonutChart';
import { NoDataView } from './VenueWiseReport';

interface AttendanceOverViewProps {
    data: any;
    loading: boolean;
    chartData: ChartDataItem[];
    autoRefresh: boolean;
    setAutoRefresh: (value: boolean) => void;
    getData: () => void;
    setNextRefreshIn: (seconds: number) => void;
    lastSynced: any;
    nextRefreshIn: any;
}

export default function AttendanceOverview({ data, loading, chartData, autoRefresh, setAutoRefresh, getData, setNextRefreshIn, lastSynced, nextRefreshIn }: AttendanceOverViewProps) {
    return (
        <>
            <Card
                className="mt-4"
                style={{
                    borderRadius: "16px",
                    border: "1px solid #e0f2fe",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                }}
            >
                <Card.Body className="p-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h5 className="fw-bold mb-0" style={{ fontSize: '18px', color: '#374151' }}>Attendance Overview</h5>
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                                // style={{
                                //     backgroundColor: autoRefresh ? '#f0fdf4' : '#f9fafb',
                                //     border: `1px solid ${autoRefresh ? '#bbf7d0' : '#e5e7eb'}`,
                                //     cursor: 'pointer',
                                // }}
                                onClick={() => {
                                    setAutoRefresh(!autoRefresh);
                                    if (!autoRefresh) setNextRefreshIn(120);
                                }}
                                title={`Auto-refresh ${autoRefresh ? 'enabled' : 'disabled'}`}
                            >
                                <FontAwesomeIcon
                                    icon={autoRefresh ? faToggleOn : faToggleOff}
                                    className={autoRefresh ? 'text-success' : 'text-muted'}
                                    style={{ fontSize: '20px' }}
                                />
                                <small style={{ fontWeight: 500 }}>Auto-refresh</small>
                            </div>

                            <div
                                className="d-flex align-items-center justify-content-center"
                                // style={{
                                //     width: '36px',
                                //     height: '36px',
                                //     borderRadius: '50%',
                                //     backgroundColor: '#eff6ff',
                                //     border: '1px solid #bfdbfe',
                                //     cursor: 'pointer',
                                // }}
                                onClick={() => {
                                    getData();
                                    if (autoRefresh) setNextRefreshIn(120);
                                }}
                                title="Refresh now"
                            >
                                <FontAwesomeIcon
                                    icon={faRefresh}
                                    className={`text-primary ${loading ? 'fa-spin' : ''}`}
                                    style={{ fontSize: '14px' }}
                                />
                            </div>
                        </div>
                    </div>

                    <Row className="align-items-center">
                        <Col md={5} className="d-flex align-items-center justify-content-center">
                            {chartData.length > 0 && data ? (
                                <div style={{ width: '100%' }}>
                                    <DonutChart
                                        data={chartData}
                                        centerText={{
                                            label: "Total Candidates",
                                            value: data?.total_count || 0
                                        }}
                                        height={240}
                                        showTooltip={true}
                                        showLegend={false}
                                        showCalloutLabels={false}
                                    />
                                    <div className="d-flex justify-content-center gap-4 mt-1">
                                        {chartData.map((item, i) => (
                                            <div key={i} className="d-flex align-items-center gap-2">
                                                <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.fill, flexShrink: 0 }} />
                                                <small style={{ color: '#6b7280', fontSize: '12px' }}>{item.category}</small>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <NoDataView message="No attendance data available for the selected shift." />
                            )}
                        </Col>
                        <Col md={7}>
                            {data ? (
                                <div className="d-flex flex-column gap-3">
                                    <StatsCard
                                        title="Candidates"
                                        value={data?.total_count || 0}
                                        icon={faUser}
                                        iconColor="primary"
                                        valueColor="primary"
                                    />
                                    <StatsCard
                                        title="Present Candidates"
                                        value={data?.present_count || 0}
                                        synced_count={data?.synced_count || 0}
                                        icon={faUserCircle}
                                        iconColor="success"
                                        valueColor="success"
                                    />
                                    <StatsCardWithProgress
                                        title="Attendance Rate"
                                        value={`${((data?.present_count || 0) / (data?.total_count || 1) * 100).toFixed(2)}%`}
                                        progress={(data?.present_count || 0) / (data?.total_count || 1) * 100}
                                        icon={faBookOpen}
                                        iconColor="success"
                                        valueColor="success"
                                    />
                                </div>
                            ) : (
                                <NoDataView message="No attendance data available for the selected shift." />
                            )}
                        </Col>
                    </Row>

                    {lastSynced && (
                        <div className="mt-3 pt-3 d-flex align-items-center justify-content-end" style={{ borderTop: '1px solid #f0f9ff' }}>
                            <FontAwesomeIcon icon={faClock} className="text-muted me-2" style={{ fontSize: '12px' }} />
                            <small className="text-muted">
                                Last synced: {moment(lastSynced).format('DD MMM YYYY, hh:mm:ss')}
                                {autoRefresh && (
                                    <span className="ms-2">
                                        • Next refresh in: <span className="fw-bold text-primary">{formatCountdown(nextRefreshIn)}</span>
                                    </span>
                                )}
                            </small>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    )
}

const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};